import { apiErrorMessage } from "@/lib/api-error";

export type AuthSession = {
  name: string;
  email: string;
};

export const AUTH_SESSION_KEY = "prvbuy:auth-session";
export const AUTH_CHANGE_EVENT = "prvbuy:auth-change";
export const AUTH_SESSION_REFRESH_TTL_MS = 60_000;

let logoutRequest: Promise<void> | null = null;
let sessionRefreshRequest: Promise<AuthSession | null> | null = null;
let verifiedSession: AuthSession | null = null;
let verifiedAt = 0;

export function readAuthSession(): AuthSession | null {
  if (typeof window === "undefined") return null;

  try {
    const value = window.localStorage.getItem(AUTH_SESSION_KEY);
    if (!value) return null;
    const session = JSON.parse(value) as Partial<AuthSession>;
    if (!session.email || !session.name) return null;
    return { email: session.email, name: session.name };
  } catch {
    return null;
  }
}

export function writeAuthSession(session: AuthSession) {
  verifiedSession = session;
  verifiedAt = Date.now();
  window.localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

export function clearAuthSession() {
  verifiedSession = null;
  verifiedAt = 0;
  window.localStorage.removeItem(AUTH_SESSION_KEY);
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

export function readVerifiedAuthSession() {
  return verifiedSession;
}

export function ensureFreshAuthSession() {
  if (verifiedSession && Date.now() - verifiedAt < AUTH_SESSION_REFRESH_TTL_MS) {
    return Promise.resolve(verifiedSession);
  }
  return refreshAuthSession();
}

export function refreshAuthSession(): Promise<AuthSession | null> {
  if (sessionRefreshRequest) return sessionRefreshRequest;

  sessionRefreshRequest = (async () => {
    const response = await fetch("/api/auth/session", {
      cache: "no-store",
      credentials: "same-origin",
    });
    const payload = await response.json().catch(() => null);

    if (response.status === 401 || response.status === 403) {
      clearAuthSession();
      return null;
    }
    if (!response.ok || !payload?.ok || !payload.user?.email || !payload.user?.displayName) {
      throw new Error(apiErrorMessage(payload, response.status, "Your account session could not be refreshed."));
    }

    const session = {
      email: String(payload.user.email),
      name: String(payload.user.displayName),
    } satisfies AuthSession;
    writeAuthSession(session);
    return session;
  })().finally(() => {
    sessionRefreshRequest = null;
  });

  return sessionRefreshRequest;
}

export function revokeCustomerSession() {
  if (logoutRequest) return logoutRequest;

  logoutRequest = (async () => {
    const response = await fetch("/api/auth/session", {
      method: "DELETE",
      cache: "no-store",
      credentials: "same-origin",
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload?.ok) {
      throw new Error(apiErrorMessage(payload, response.status, "Sign out could not be completed. Your local session remains unchanged; retry before closing this device session."));
    }
  })().finally(() => {
    logoutRequest = null;
  });

  return logoutRequest;
}

export function nameFromEmail(email: string) {
  const localPart = email.split("@")[0]?.trim() || "PRVBUY Member";
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
